import { CustomerUiProvider } from "@babackapp/customer-ui/components/provider";
import { TooltipProvider } from "@babackapp/customer-ui/components/tooltip";
import {
  configureMerchantToken,
  configureReturnWidgetBaseUrl,
} from "@babackapp/private-api-client/client/return-widget-client";
import { usePublicShopSuspense } from "@babackapp/private-api-client/client/use-shop-suspense";
import { tokenManager } from "@babackapp/auth-client/utils";
import type { EmbedToWidgetMessage } from "@babackapp/return-widget-components/types/shop-now";
import { useQueryClient } from "@tanstack/react-query";
import { createStore, Provider } from "jotai";
import { AnimatePresence, motion } from "motion/react";
import { type ReactNode, Suspense, useEffect, useRef, useState } from "react";
import { useLocation, useOutlet, useParams } from "react-router-dom";
import { fetchWidgetAuthCheck, useWidgetAuthCheck } from "../hooks/use-widget-auth-check";
import { AdminBadge } from "./admin-badge";
import { WidgetErrorBoundary } from "./widget-error-boundary";

// Configure the return widget API client to use the same API URL and merchant
// token as the rest of baback-office, so CORS and auth work correctly.
configureReturnWidgetBaseUrl(import.meta.env.VITE_API_URL ?? "https://api.baback.co/");
configureMerchantToken(() => tokenManager.getAccessToken());

const widgetStore = createStore();

const API_BASE = (import.meta.env.VITE_API_URL ?? "https://api.baback.co/").replace(/\/$/, "");

export function ReturnWidgetLayout() {
  const segment = useLocation().pathname.split("/")[3];
  const outlet = useOutlet();
  const { data: authCheck } = useWidgetAuthCheck();
  const isMerchant = authCheck?.isAuthenticated ?? false;

  console.log("[widget-auth] ReturnWidgetLayout render — authCheck:", authCheck, "isMerchant:", isMerchant);

  return (
    <Provider store={widgetStore}>
      <TooltipProvider>
        <WidgetErrorBoundary>
          <Suspense fallback={<div className="h-screen w-screen bg-white" />}>
            <ThemedLayout segment={segment} outlet={outlet} isMerchant={isMerchant} />
          </Suspense>
        </WidgetErrorBoundary>
      </TooltipProvider>
    </Provider>
  );
}

function ThemedLayout({
  segment,
  outlet,
  isMerchant,
}: {
  segment: string;
  outlet: ReactNode;
  isMerchant: boolean;
}) {
  const { shopId } = useParams<{ shopId: string }>();
  const { data: shop } = usePublicShopSuspense(shopId!);
  const [parentFont, setParentFont] = useState<string | undefined>();
  const queryClient = useQueryClient();
  const storageAccessDone = useRef(false);

  useEffect(() => {
    console.log("[widget-auth] ThemedLayout mounted");
    console.log("[widget-auth] requestStorageAccess available:", !!document.requestStorageAccess);
    console.log("[widget-auth] document.cookie on mount:", document.cookie);
    console.log("[widget-auth] tokenManager.getAccessToken() on mount:", tokenManager.getAccessToken());

    // Check current storage access state
    if (document.hasStorageAccess) {
      document.hasStorageAccess().then((has) => {
        console.log("[widget-auth] hasStorageAccess() on mount:", has);
      });
    }

    if (!document.requestStorageAccess) {
      console.log("[widget-auth] requestStorageAccess not available, skipping");
      return;
    }

    function handleFirstClick(e: MouseEvent) {
      console.log("[widget-auth] click event fired on:", (e.target as Element)?.tagName, "storageAccessDone:", storageAccessDone.current);
      if (storageAccessDone.current) return;
      storageAccessDone.current = true;

      console.log("[widget-auth] calling requestStorageAccess()");
      document.requestStorageAccess()
        .then(() => {
          console.log("[widget-auth] requestStorageAccess GRANTED");
          console.log("[widget-auth] document.cookie after grant:", document.cookie);
          console.log("[widget-auth] token after grant:", tokenManager.getAccessToken());
          return fetchWidgetAuthCheck();
        })
        .then((result) => {
          console.log("[widget-auth] fetchWidgetAuthCheck result:", result);
          queryClient.setQueryData(["widget-auth-check"], result);
        })
        .catch((err) => {
          console.log("[widget-auth] requestStorageAccess REJECTED:", err);
        });
    }

    window.addEventListener("click", handleFirstClick, { once: true });
    return () => window.removeEventListener("click", handleFirstClick);
  }, [queryClient]);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const msg = event.data as EmbedToWidgetMessage;
      if (msg?.type !== "WIDGET_FONT") return;

      if (msg.fontFaceUrl) {
        const face = new FontFace(msg.fontFamily, `url(${msg.fontFaceUrl})`);
        face.load().then((f) => document.fonts.add(f)).catch(() => {});
      }
      setParentFont(msg.fontFamily);
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const primary = shop?.preferences?.customisation?.primary_600;
  const customCssUrl = shop?.preferences?.customCss
    ? `${API_BASE}/public/custom-css/${shopId}.css`
    : undefined;

  return (
    <CustomerUiProvider
      className="h-screen w-screen bg-white flex flex-col overflow-hidden"
      colors={primary ? { primary } : undefined}
      fonts={parentFont ? { sans: parentFont } : undefined}
      customCssUrl={customCssUrl}
    >
      <div className="relative flex-1 min-h-0 flex flex-col">
        {isMerchant && <AdminBadge />}
        <div className="flex-1 min-h-0 z-0 relative overflow-y-auto flex flex-col p-8 pb-16">
          <WidgetErrorBoundary>
            <Suspense>
              <AnimatePresence mode="wait" propagate>
                <motion.div key={segment} className="flex-1 min-h-0 w-full flex flex-col">
                  {outlet}
                </motion.div>
              </AnimatePresence>
            </Suspense>
          </WidgetErrorBoundary>
        </div>
      </div>
    </CustomerUiProvider>
  );
}
